<template>
  <div>
    <div class="configurator-actions">
      <div>
        <div class="configurator-title">
          {{ $t('configurator.configuration_title') }}
        </div>
        <div class="configurator-subtitle">
          {{ $t('configurator.configuration_subtitle') }}
        </div>
      </div>
      <div class="d-flex">
        <b-button
          variant="outline-secondary"
          class="cfg-btn mr-50"
          @click="$emit('prev')"
        >
          ← {{ $t('configurator.add_more') }}
        </b-button>
        <b-button
          variant="primary"
          class="cfg-btn"
          @click="$emit('next')"
        >
          {{ $t('configurator.next') }} → {{ $t('configurator.step_extras') }}
        </b-button>
      </div>
    </div>

    <div class="configuration-layout">
      <!-- Cortinas de la oferta -->
      <div class="cfg-card list-card">
        <div class="sidebar-header">
          {{ $t('configurator.offer_curtains') }}
        </div>
        <div
          v-for="group in groups"
          :key="group.id"
          class="curtain-group"
        >
          <div class="curtain-group-label">
            <span class="curtain-group-badge">{{ group.key }}</span>
            <span class="text-truncate">{{ group.curtain ? group.curtain.ref : '' }}</span>
            <span
              v-if="group.curtain && group.curtain.sin_tarifa"
              class="cfg-badge cfg-badge-orange ml-auto"
            >{{ $t('configurator.price_pending') }}</span>
          </div>
          <div class="curtain-subitems">
            <div
              v-for="item in group.items"
              :key="item.key"
              class="curtain-item clickable"
              :class="{ active: activeKey === item.key }"
              @click="activeKey = item.key"
            >
              <div class="curtain-item-id">
                {{ item.key }}
              </div>
              <div class="curtain-item-info">
                <div class="curtain-item-name">
                  {{ item.key }}
                </div>
                <div class="curtain-item-size">
                  {{ sizeText(item) }}
                </div>
              </div>
              <div
                class="curtain-item-dot"
                :class="isItemComplete(item) ? 'ok' : 'warn'"
              />
            </div>
          </div>
        </div>
        <div class="p-75">
          <b-button
            variant="primary"
            block
            class="cfg-btn"
            @click="$emit('prev')"
          >
            <feather-icon
              icon="PlusIcon"
              size="14"
            />
            {{ $t('configurator.add_other_curtain') }}
          </b-button>
        </div>
      </div>

      <!-- Configuración de la cortina activa -->
      <div
        v-if="active"
        class="cfg-card"
      >
        <div class="cfg-card-header">
          <div class="d-flex align-items-center">
            <div class="product-icon mr-75">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              ><rect
                x="3"
                y="3"
                width="18"
                height="18"
                rx="2"
              /><line
                x1="3"
                y1="9"
                x2="21"
                y2="9"
              /></svg>
            </div>
            <div>
              <div class="product-title">
                {{ activeCurtain.name }}
              </div>
              <div class="cfg-hint">
                {{ $t('configurator.editing', { key: active.key }) }}
              </div>
            </div>
          </div>
          <div class="d-flex">
            <b-button
              v-b-tooltip.hover
              variant="outline-secondary"
              class="btn-icon mr-50"
              :title="$t('configurator.duplicate')"
              @click="duplicate(active.key)"
            >
              <feather-icon
                icon="CopyIcon"
                size="14"
              />
            </b-button>
            <b-button
              v-b-tooltip.hover
              variant="outline-danger"
              class="btn-icon"
              :title="$t('configurator.remove')"
              @click="remove(active.key)"
            >
              <feather-icon
                icon="TrashIcon"
                size="14"
              />
            </b-button>
          </div>
        </div>

        <div
          v-if="pricePending(active)"
          class="px-2 pt-1"
        >
          <div class="budget-warning">
            <feather-icon
              icon="InfoIcon"
              size="14"
            />
            {{ $t('configurator.price_pending_help') }}
          </div>
        </div>

        <div class="config-block">
          <div class="config-block-title">
            {{ $t('configurator.scheme') }}
          </div>
          <div class="measure-diagram">
            <img
              :src="schemeImage"
              alt=""
            >
          </div>
          <div
            v-if="serieImage"
            class="serie-image"
          >
            <div class="cfg-label">
              {{ $t('configurator.series') }} {{ active.serie.toUpperCase() }}
            </div>
            <img
              :src="serieImage"
              alt=""
            >
          </div>
          <div class="cfg-label">
            {{ $t('configurator.measure_type') }}
          </div>
          <div class="radio-group">
            <div
              v-for="option in measureOptions"
              :key="option.value"
              class="radio-option"
              :class="{ selected: active.measure === option.value }"
              @click="update({ measure: option.value })"
            >
              {{ option.text }}
            </div>
          </div>
        </div>

        <div class="config-block">
          <div class="config-block-title">
            {{ $t('configurator.measures') }}
          </div>
          <div class="measures-grid">
            <div>
              <div class="cfg-label">
                {{ $t('configurator.length') }} (mm)
              </div>
              <b-form-input
                type="number"
                class="input-large"
                placeholder="ej. 3200"
                :value="active.measure === 'L2' ? active.l2 : active.l1"
                :state="errors.length ? false : null"
                @input="typeMeasure(active.measure === 'L2' ? 'l2' : 'l1', $event)"
                @change="recalculate(active.key)"
              />
              <small
                v-if="errors.length"
                class="text-danger d-block mt-25"
              >{{ $t(errors.length) }}</small>
              <div
                v-else
                class="cfg-hint mt-25"
              >
                {{ active.serie ? $t('configurator.min_length') : $t('configurator.max_length') }}
              </div>
            </div>
            <div>
              <div class="cfg-label">
                {{ $t('configurator.height') }} (mm)
              </div>
              <b-form-input
                type="number"
                class="input-large"
                placeholder="ej. 2400"
                :value="active.measure === 'L2' ? active.h2 : active.h1"
                :state="errors.height ? false : null"
                @input="typeMeasure(active.measure === 'L2' ? 'h2' : 'h1', $event)"
                @change="recalculate(active.key)"
              />
              <small
                v-if="errors.height"
                class="text-danger d-block mt-25"
              >{{ $t(errors.height) }}</small>
              <div
                v-else
                class="cfg-hint mt-25"
              >
                {{ $t('configurator.max_height', { max: active.serie ? '10.000' : '8.000' }) }}
              </div>
            </div>
          </div>
          <div
            v-if="active.calcError"
            class="budget-warning budget-error mt-1"
          >
            {{ active.calcError }}
          </div>
          <a
            v-if="groupOf(active).items.length > 1"
            href="#"
            class="d-inline-block small font-weight-bold mt-1"
            @click.prevent="applyToGroup"
          >
            {{ $t('configurator.apply_to_group', { group: `C${active.group}` }) }}
          </a>
        </div>

        <div
          v-if="active.control_box"
          class="config-block"
        >
          <div class="config-block-title">
            {{ $t('configurator.motor_position') }}
          </div>
          <div class="radio-group">
            <div
              class="radio-option"
              :class="{ selected: active.position === 'Izquierda' }"
              @click="update({ position: 'Izquierda' })"
            >
              <feather-icon
                icon="ChevronLeftIcon"
                size="14"
              />
              {{ $t('configurator.left') }}
            </div>
            <div
              class="radio-option"
              :class="{ selected: active.position === 'Derecha' }"
              @click="update({ position: 'Derecha' })"
            >
              {{ $t('configurator.right') }}
              <feather-icon
                icon="ChevronRightIcon"
                size="14"
              />
            </div>
          </div>
        </div>

        <!-- Datos técnicos calculados (como el resumen de cortina del tarificador anterior) -->
        <div
          v-if="active.calc || guidesOptional || needsIrrigation"
          class="config-block"
        >
          <div class="config-block-title">
            {{ $t('configurator.technical_data') }}
          </div>
          <div
            v-if="active.calc"
            class="tech-grid"
          >
            <div class="tech-row">
              <span>{{ $t('configurator.length') }} (L1)</span><strong>{{ active.calc.l1 }} mm</strong>
            </div>
            <div class="tech-row">
              <span>{{ $t('configurator.height') }} (H1)</span><strong>{{ active.calc.h1 }} mm</strong>
            </div>
            <div class="tech-row">
              <span>{{ $t('configurator.length') }} (L2)</span><strong>{{ active.calc.l2 }} mm</strong>
            </div>
            <div class="tech-row">
              <span>{{ $t('configurator.height') }} (H2)</span><strong>{{ active.calc.h2 }} mm</strong>
            </div>
            <div
              v-if="active.calc.cajon"
              class="tech-row"
            >
              <span>{{ $t('configurator.drawer') }}</span><strong>{{ active.calc.cajon }}</strong>
            </div>
            <div class="tech-row">
              <span>{{ $t('configurator.side_guides') }}</span>
              <strong v-if="active.calc.guias">{{ active.calc.guias }}</strong>
              <strong v-else-if="guidesOptional">{{ $t('configurator.guides_not_included') }}</strong>
              <strong v-else>—</strong>
            </div>
            <div
              v-if="active.calc.motor"
              class="tech-row"
            >
              <span>{{ $t('configurator.motor') }}</span><strong>{{ active.calc.motor }} ×{{ active.calc.num_motor }}</strong>
            </div>
            <div
              v-if="active.position"
              class="tech-row"
            >
              <span>{{ $t('configurator.motor_position') }}</span><strong>{{ active.position === 'Izquierda' ? $t('configurator.left') : $t('configurator.right') }}</strong>
            </div>
          </div>
          <div
            v-if="guidesOptional"
            class="budget-note mt-1"
          >
            <feather-icon
              icon="AlertCircleIcon"
              size="14"
            />
            {{ $t('configurator.guides_optional_notice') }}
          </div>
          <template v-if="needsIrrigation">
            <div class="budget-warning mt-1">
              <feather-icon
                icon="AlertTriangleIcon"
                size="14"
              />
              {{ $t('configurator.ei180_notice') }}
            </div>
            <div class="budget-note">
              <feather-icon
                icon="AlertCircleIcon"
                size="14"
              />
              {{ $t('configurator.ei180_supply_notice') }}
            </div>
          </template>
        </div>

        <div class="config-block">
          <div class="config-block-title">
            {{ $t('configurator.technical_notes') }}
          </div>
          <b-form-textarea
            rows="2"
            :value="active.notes"
            :placeholder="$t('configurator.technical_notes_placeholder')"
            @change="updateItem({ key: active.key, changes: { notes: $event } })"
          />
        </div>
      </div>

      <!-- Estado de la configuración (sin precios) -->
      <div class="cfg-card">
        <div class="budget-header">
          <span class="budget-title">{{ $t('configurator.status') }}</span>
        </div>
        <div class="budget-lines">
          <div
            v-for="item in items"
            :key="item.key"
            class="budget-line"
          >
            <span class="budget-line-name">{{ item.key }} · {{ item.ref }}</span>
            <span
              v-if="pricePending(item)"
              class="cfg-badge cfg-badge-orange"
            >{{ $t('configurator.price_pending') }}</span>
            <feather-icon
              v-else-if="isItemComplete(item)"
              icon="CheckIcon"
              size="13"
              class="text-success"
            />
            <feather-icon
              v-else
              icon="AlertTriangleIcon"
              size="13"
              class="text-warning"
            />
          </div>
        </div>
        <div class="budget-stats">
          <div class="budget-stat">
            <span class="budget-stat-label">{{ $t('configurator.groups') }}</span>
            <span class="budget-stat-value">{{ groups.length }} ({{ items.length }} ud.)</span>
          </div>
          <div class="budget-stat">
            <span class="budget-stat-label">{{ $t('configurator.curtains') }}</span>
            <span class="budget-stat-value">{{ items.length }}</span>
          </div>
          <div class="budget-stat">
            <span class="budget-stat-label">{{ $t('configurator.configured') }}</span>
            <span class="budget-stat-value">{{ completed }} / {{ items.length }}</span>
          </div>
        </div>
        <div class="px-1 pb-1">
          <div class="budget-note">
            <feather-icon
              icon="AlertCircleIcon"
              size="14"
            />
            {{ $t('configurator.prices_in_summary') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import {
  BButton, BFormInput, BFormTextarea, VBTooltip,
} from 'bootstrap-vue'
import scheme from '@/assets/images/rater/medidas.png'
import schemeDouble from '@/assets/images/rater/medidas_doble.png'
import schemeDoubleVertical from '@/assets/images/rater/medidas_doble_v_vf.png'
import serieH from '@/assets/images/rater/H-mini.jpg'
import serieV from '@/assets/images/rater/V-mini.jpg'
import serieVF from '@/assets/images/rater/VF-mini.jpg'

// Mismas imágenes que el tarificador anterior (steps/Second.vue y steps/Summary.vue)
const SERIE_IMAGES = { h: serieH, v: serieV, vf: serieVF }

export default {
  components: {
    BButton, BFormInput, BFormTextarea,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      activeKey: null,
    }
  },
  computed: {
    ...mapGetters({
      items: 'configurator/getItems',
      ssbPriceMissing: 'configurator/ssbPriceMissing',
      groups: 'configurator/getGroups',
      catalog: 'configurator/getCatalog',
      isItemComplete: 'configurator/isItemComplete',
      measureErrors: 'configurator/measureErrors',
    }),
    errors() {
      return this.measureErrors(this.active)
    },
    // Según las reglas del catálogo: las guías opcionales (MSB) y la irrigación (EI-180)
    extraProducts() {
      return this.catalog.extras.flatMap(category => category.products.map(product => ({ ...product, category: category.ref })))
    },
    guidesOptional() {
      return this.extraProducts.some(p => p.rules.auto_by_height && (p.rules.applies_to || []).includes(this.active.productRef))
    },
    needsIrrigation() {
      return this.extraProducts.some(p => p.category === 'irrigacion' && (p.rules.applies_to || []).includes(this.active.productRef))
    },
    active() {
      return this.items.find(i => i.key === this.activeKey)
    },
    activeCurtain() {
      return this.groupOf(this.active).curtain || {}
    },
    measureOptions() {
      return [
        { text: 'L1 / H1', value: 'L1' },
        { text: 'L2 / H2', value: 'L2' },
      ]
    },
    schemeImage() {
      if (!this.active.serie) return scheme
      return this.active.serie === 'h' ? schemeDouble : schemeDoubleVertical
    },
    serieImage() {
      return SERIE_IMAGES[this.active.serie] || null
    },
    completed() {
      return this.items.filter(i => this.isItemComplete(i)).length
    },
  },
  methods: {
    ...mapMutations({
      updateItem: 'configurator/updateItem',
      removeItem: 'configurator/removeItem',
      duplicateItem: 'configurator/duplicateItem',
    }),
    ...mapActions({
      calculateItem: 'configurator/calculateItem',
      resolveBoxesOf: 'configurator/resolveBoxesOf',
    }),
    // Sin tarifa o con una medida que la tarifa del cliente no cubre
    pricePending(item) {
      return item.sin_tarifa || !!(item.calc && item.calc.price_pending) || (item.price_by === 'client_m2' && this.ssbPriceMissing)
    },
    groupOf(item) {
      return this.groups.find(g => g.id === item.group) || { items: [] }
    },
    toNumber(value) {
      return value === '' ? null : Number(value)
    },
    sizeText(item) {
      const l = item.measure === 'L2' ? item.l2 : item.l1
      const h = item.measure === 'L2' ? item.h2 : item.h1
      return l && h ? `${l} × ${h} mm` : this.$t('configurator.no_measures')
    },
    async update(changes) {
      const { key } = this.active
      this.updateItem({ key, changes })
      await this.recalculate(key)
    },
    // Valida en cada pulsación y recalcula en la API al dejar de escribir
    typeMeasure(field, value) {
      const { key } = this.active
      this.updateItem({ key, changes: { [field]: this.toNumber(value), calc: null, calcError: null } })
      clearTimeout(this.calcTimer)
      this.calcTimer = setTimeout(() => this.recalculate(key), 600)
    },
    async recalculate(key) {
      clearTimeout(this.calcTimer)
      await this.calculateItem(key)
      await this.resolveBoxesOf(key)
    },
    async applyToGroup() {
      const source = this.active
      const changes = {
        measure: source.measure, l1: source.l1, h1: source.h1, l2: source.l2, h2: source.h2, position: source.position,
      }
      const targets = this.groupOf(source).items.filter(i => i.key !== source.key)
      await Promise.all(targets.map(async item => {
        this.updateItem({ key: item.key, changes })
        await this.calculateItem(item.key)
        await this.resolveBoxesOf(item.key)
      }))
    },
    duplicate(key) {
      this.duplicateItem(key)
      const group = this.groupOf(this.active)
      this.activeKey = group.items[group.items.length - 1].key
    },
    remove(key) {
      this.removeItem(key)
      this.activeKey = this.items.length ? this.items[0].key : null
      if (!this.items.length) this.$emit('prev')
    },
  },
  created() {
    const focus = this.$store.state.configurator.focusKey
    if (focus && this.items.some(i => i.key === focus)) this.activeKey = focus
    else if (this.items.length) this.activeKey = this.items[0].key
    this.$store.commit('configurator/setFocus', null)
  },
  beforeDestroy() {
    clearTimeout(this.calcTimer)
  },
}
</script>

<style lang="scss" scoped>
.configuration-layout {
  display: grid;
  grid-template-columns: 220px 1fr 260px;
  gap: 1.25rem;
  align-items: start;
  @media (max-width: 1199px) {
    grid-template-columns: 1fr;
  }
}
.list-card {
  overflow-y: auto;
  max-height: calc(100vh - 180px);
}
.sidebar-header {
  padding: .75rem 1rem .5rem;
  font-size: .7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .8px;
  color: #82868b;
}
.curtain-group {
  margin-bottom: .25rem;
}
.curtain-group-label {
  display: flex;
  align-items: center;
  gap: .5rem;
  padding: .45rem .75rem;
  font-size: .75rem;
  font-weight: 700;
  color: #6e6b7b;
}
.curtain-group-badge {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: #f0f0f0;
  font-size: .7rem;
  font-weight: 800;
  color: #4d4d4d;
}
.curtain-subitems {
  display: flex;
  flex-direction: column;
  gap: .25rem;
  padding: 0 .5rem .25rem 1.25rem;
}
.product-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #fef3e2;
  color: #ac1a19;
}
.product-title {
  font-size: .875rem;
  font-weight: 700;
  color: #1e1e1e;
}
.measure-diagram {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  margin-bottom: .875rem;
  padding: 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  background: #f6f6f6;
  img {
    max-width: 100%;
    max-height: 200px;
    object-fit: contain;
  }
}
.serie-image {
  margin-bottom: .875rem;
  text-align: center;
  img {
    max-width: 160px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
  }
}
.tech-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 1.25rem;
  @media (max-width: 575px) {
    grid-template-columns: 1fr;
  }
}
.tech-row {
  display: flex;
  justify-content: space-between;
  padding: .4rem 0;
  border-bottom: 1px solid #f0f0f0;
  font-size: .8125rem;
  span {
    color: #6e6b7b;
  }
  strong {
    color: #1e1e1e;
  }
}
.measures-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: .875rem;
}
.input-large {
  height: auto;
  padding: .65rem .875rem;
  font-size: 1.1rem;
  font-weight: 600;
}
</style>
